import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../constants';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

interface Product {
  _id: string;
  modelName: string;
  productCode?: string;
  prototypeCode?: string;
  status: string;
  modelType: string;
  fabric: string;
  isPrototype: boolean;
}

const TechnicalSheetList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useContext(AuthContext);

  const canEditTechnicalSheet = isAdmin || hasPermission('designer', 'update');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Tüm ürünleri getir (prototip veya normal)
      const response = await fetch(`${API_URL}/products`);
      
      if (!response.ok) {
        throw new Error('Ürünler alınırken bir hata oluştu');
      }
      
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      console.error('Veri çekme hatası:', err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(
    product => 
      product.modelName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.productCode && product.productCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.prototypeCode && product.prototypeCode.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Teknik Föyler</h1>
      </div>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Ürün adı veya kodu ile ara..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-xl text-red-600">{error}</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Sonuç bulunamadı</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ürün Adı
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kod
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tip
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={product._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{product.modelName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {product.isPrototype 
                        ? `P: ${product.prototypeCode || 'Belirtilmemiş'}`
                        : `Ü: ${product.productCode || 'Belirtilmemiş'}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {product.modelType === 'knitting' ? 'Örme' : 'Dokuma'} / {product.fabric}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${product.status === 'active' ? 'bg-green-100 text-green-800' : 
                        product.status === 'archived' ? 'bg-gray-100 text-gray-800' : 
                        'bg-yellow-100 text-yellow-800'}`}>
                      {product.status === 'active' ? 'Aktif' : 
                       product.status === 'archived' ? 'Arşivlendi' : 'Taslak'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <button
                      onClick={() => navigate(`/technical-sheet/${product._id}`)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Görüntüle
                    </button>
                    {canEditTechnicalSheet && (
                      <button
                        onClick={() => navigate(`/technical-sheet/${product._id}/edit`)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Düzenle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TechnicalSheetList;
